// Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

// Authentication state
let currentUser = null;
let authToken = null;

// Global state
let currentProject = null;

// Progress Bar Component
class ProgressBar {
    constructor(container, options = {}) {
        this.container = container;
        this.options = {
            showPercentage: true,
            showSteps: false,
            steps: [],
            animated: true,
            ...options
        };
        this.currentStep = 0;
        this.percentage = 0;
        this.init();
    }

    init() {
        this.container.innerHTML = `
            <div class="progress-text">
                <span class="progress-message">${this.options.message || 'Processing...'}</span>
                ${this.options.showPercentage ? '<span class="progress-percentage">0%</span>' : ''}
            </div>
            <div class="progress-bar">
                <div class="progress-fill" style="width: 0%"></div>
            </div>
            ${this.options.showSteps ? '<div class="progress-steps"></div>' : ''}
        `;

        this.progressFill = this.container.querySelector('.progress-fill');
        this.progressMessage = this.container.querySelector('.progress-message');
        this.progressPercentage = this.container.querySelector('.progress-percentage');
        this.progressSteps = this.container.querySelector('.progress-steps');

        if (this.options.showSteps && this.options.steps.length > 0) {
            this.renderSteps();
        }
    }

    renderSteps() {
        if (!this.progressSteps) return;

        this.progressSteps.innerHTML = this.options.steps
            .map((step, index) => {
                let className = 'progress-step';
                if (index < this.currentStep) className += ' completed';
                if (index === this.currentStep) className += ' active';
                return `<span class="${className}">${step}</span>`;
            })
            .join('');
    }

    updateProgress(percentage, message) {
        this.percentage = Math.max(0, Math.min(100, percentage));

        if (this.progressFill) {
            this.progressFill.style.width = `${this.percentage}%`;
        }

        if (this.progressPercentage && this.options.showPercentage) {
            this.progressPercentage.textContent = `${Math.round(this.percentage)}%`;
        }

        if (message && this.progressMessage) {
            this.progressMessage.textContent = message;
        }
    }

    nextStep(message) {
        if (this.currentStep < this.options.steps.length - 1) {
            this.currentStep++;
            this.renderSteps();

            const stepProgress = (this.currentStep / this.options.steps.length) * 100;
            this.updateProgress(stepProgress, message || this.options.steps[this.currentStep]);
        }
    }

    complete(message = 'Complete!') {
        this.updateProgress(100, message);
        this.currentStep = this.options.steps.length;
        this.renderSteps();

        setTimeout(() => {
            this.container.style.display = 'none';
        }, 2000);
    }

    reset() {
        this.percentage = 0;
        this.currentStep = 0;
        this.updateProgress(0, this.options.message || 'Processing...');
        this.renderSteps();
        this.container.style.display = 'block';
    }

    hide() {
        this.container.style.display = 'none';
    }

    show() {
        this.container.style.display = 'block';
    }
}

// View management
let currentView = 'main';
const views = {
    main: document.getElementById('mainView'),
    projectDetails: document.getElementById('projectDetailsView'),
    fileSummary: document.getElementById('fileSummaryView'),
    admin: document.getElementById('adminView')
};

// DOM Elements
const elements = {
    // Authentication
    loginView: document.getElementById('loginView'),
    appContainer: document.getElementById('appContainer'),
    loginForm: document.getElementById('loginForm'),
    loginEmail: document.getElementById('loginEmail'),
    loginPassword: document.getElementById('loginPassword'),
    loginBtn: document.getElementById('loginBtn'),
    loginError: document.getElementById('loginError'),
    logoutBtn: document.getElementById('logoutBtn'),
    userDisplayName: document.getElementById('userDisplayName'),
    tenantDisplayName: document.getElementById('tenantDisplayName'),

    // Project creation
    projectName: document.getElementById('projectName'),
    projectDescription: document.getElementById('projectDescription'),
    createProjectBtn: document.getElementById('createProjectBtn'),
    createProjectProgress: document.getElementById('createProjectProgress'),
    
    // Projects list
    projectsList: document.getElementById('projectsList'),
    refreshProjectsBtn: document.getElementById('refreshProjectsBtn'),
    
    // Project details
    backBtn: document.getElementById('backBtn'),
    projectNameTitle: document.getElementById('projectNameTitle'),
    projectDescriptionContent: document.getElementById('projectDescriptionContent'),
    
    // File summary
    backToProjectBtn: document.getElementById('backToProjectBtn'),
    summaryFileName: document.getElementById('summaryFileName'),
    summaryContentDisplay: document.getElementById('summaryContentDisplay'),
    copySummaryTextBtn: document.getElementById('copySummaryTextBtn'),
    
    // Categories & Questions
    categoriesList: document.getElementById('categoriesList'),
    addCategoryBtn: document.getElementById('addCategoryBtn'),
    categoryModal: document.getElementById('categoryModal'),
    categoryModalTitle: document.getElementById('categoryModalTitle'),
    categoryForm: document.getElementById('categoryForm'),
    categoryId: document.getElementById('categoryId'),
    categoryProjectId: document.getElementById('categoryProjectId'),
    categoryName: document.getElementById('categoryName'),
    categoryDescription: document.getElementById('categoryDescription'),
    categoryDisplayOrder: document.getElementById('categoryDisplayOrder'),
    saveCategoryBtn: document.getElementById('saveCategoryBtn'),
    cancelCategoryBtn: document.getElementById('cancelCategoryBtn'),
    closeCategoryModal: document.getElementById('closeCategoryModal'),
    categoryFormError: document.getElementById('categoryFormError'),

    // Question modal
    categoryQuestionModal: document.getElementById('categoryQuestionModal'),
    categoryQuestionModalTitle: document.getElementById('categoryQuestionModalTitle'),
    categoryQuestionForm: document.getElementById('categoryQuestionForm'),
    questionId: document.getElementById('questionId'),
    questionCategoryId: document.getElementById('questionCategoryId'),
    questionText: document.getElementById('questionText'),
    saveQuestionToCategoryBtn: document.getElementById('saveQuestionToCategoryBtn'),
    cancelQuestionFormBtn: document.getElementById('cancelQuestionFormBtn'),
    closeCategoryQuestionModal: document.getElementById('closeCategoryQuestionModal'),
    questionFormError: document.getElementById('questionFormError'),

    // File upload
    uploadArea: document.getElementById('uploadArea'),
    fileInput: document.getElementById('fileInput'),
    uploadProgress: document.getElementById('uploadProgress'),
    filesList: document.getElementById('filesList'),
    filesCount: document.getElementById('filesCount'),
    
    // Summary generation
    generateExcelAnalysisBtn: document.getElementById('generateExcelAnalysisBtn'),
    existingExcelAnalysis: document.getElementById('existingExcelAnalysis'),
    downloadExistingAnalysisBtn: document.getElementById('downloadExistingAnalysisBtn'),
    summaryProgress: document.getElementById('summaryProgress'),
    summaryResult: document.getElementById('summaryResult'),

    // Usability report generation
    generateUsabilityReportBtn: document.getElementById('generateUsabilityReportBtn'),
    existingUsabilityReport: document.getElementById('existingUsabilityReport'),
    downloadExistingReportBtn: document.getElementById('downloadExistingReportBtn'),
    reportProgress: document.getElementById('reportProgress'),
    
    // Admin panel
    adminBtn: document.getElementById('adminBtn'),
    adminView: document.getElementById('adminView'),
    backToMainBtn: document.getElementById('backToMainBtn'),
    
    // Loading overlay
    loadingOverlay: document.getElementById('loadingOverlay'),
    
    // Toast container
    toastContainer: document.getElementById('toastContainer')
};

// Authentication Functions
class AuthManager {
    static saveToken(token) {
        localStorage.setItem('authToken', token);
        authToken = token;
    }

    static getToken() {
        if (!authToken) {
            authToken = localStorage.getItem('authToken');
        }
        return authToken;
    }

    static removeToken() {
        localStorage.removeItem('authToken');
        authToken = null;
    }

    static saveUser(user) {
        localStorage.setItem('currentUser', JSON.stringify(user));
        currentUser = user;
    }

    static getUser() {
        if (!currentUser) {
            const userData = localStorage.getItem('currentUser');
            currentUser = userData ? JSON.parse(userData) : null;
        }
        return currentUser;
    }

    static removeUser() {
        localStorage.removeItem('currentUser');
        currentUser = null;
    }

    static isAuthenticated() {
        return !!(this.getToken() && this.getUser());
    }

    static logout() {
        this.removeToken();
        this.removeUser();
        showLoginView();
    }

    static async login(email, password) {
        try {
            console.log('Attempting login with URL:', `${API_BASE_URL}/login`);
            const response = await fetch(`${API_BASE_URL}/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, password })
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Login failed');
            }

            const data = await response.json();
            this.saveToken(data.token);
            this.saveUser(data.user);

            return data;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    }
}

// View Management Functions
function showLoginView() {
    elements.loginView.style.display = 'flex';
    elements.appContainer.style.display = 'none';
    elements.loginEmail.focus();
}

function showAppView() {
    elements.loginView.style.display = 'none';
    elements.appContainer.style.display = 'block';

    // Update user display
    const user = AuthManager.getUser();
    if (user) {
        elements.userDisplayName.textContent = user.username;
        elements.tenantDisplayName.textContent = user.tenant_name || 'Organization';

        // Show/hide admin button based on role
        if (elements.adminBtn) {
            if (user.role === 'admin') {
                elements.adminBtn.style.display = '';  // Reset to default (CSS will handle)
                elements.adminBtn.style.visibility = 'visible';
            } else {
                elements.adminBtn.style.display = 'none';
            }
        }
    }

    // Load initial data
    loadProjects();
}

function showLoginError(message) {
    elements.loginError.textContent = message;
    elements.loginError.style.display = 'block';
}

function hideLoginError() {
    elements.loginError.style.display = 'none';
}

// API Functions
class ApiClient {
    static async request(endpoint, options = {}) {
        const url = `${API_BASE_URL}${endpoint}`;
        const token = AuthManager.getToken();

        const headers = {
            ...(token && { 'Authorization': `Bearer ${token}` }),
            ...options.headers
        };

        // Don't set Content-Type for FormData (file uploads) - let browser set it
        if (!(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }

        const config = {
            headers,
            ...options
        };
        
        console.log(`[ApiClient] Making request to: ${url}`);
        console.log('[ApiClient] Request config:', {
            method: config.method || 'GET',
            headers: config.headers,
            bodyType: config.body ? (config.body instanceof FormData ? 'FormData' : typeof config.body) : 'none'
        });
        
        try {
            console.log('[ApiClient] Calling fetch...');
            const response = await fetch(url, config);
            console.log('[ApiClient] Fetch completed, response status:', response.status);
            console.info("HTTP response --- " + response.status)
            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    // Unauthorized (401) or Forbidden (403) - redirect to login
                    AuthManager.logout();
                    return;
                }
                const errorText = await response.text();
                let errorMessage;
                try {
                    // Try to parse JSON error response
                    const errorData = JSON.parse(errorText);
                    errorMessage = errorData.error || errorData.message || errorText;
                } catch {
                    // If not JSON, use the text as-is
                    errorMessage = errorText;
                }
                throw new Error(`HTTP ${response.status}: ${errorMessage}`);
            }
            
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return await response.json();
            } else {
                return await response.text();
            }
        } catch (error) {
            console.error('[ApiClient] Request failed at:', error.name);
            console.error('[ApiClient] Error message:', error.message);
            console.error('[ApiClient] Full error:', error);
            throw error;
        }
    }

    static async get(endpoint) {
        return this.request(endpoint, { method: 'GET' });
    }

    static async post(endpoint, data) {
        return this.request(endpoint, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    static async put(endpoint, data) {
        return this.request(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }

    static async patch(endpoint, data) {
        return this.request(endpoint, {
            method: 'PATCH',
            body: JSON.stringify(data)
        });
    }

    static async delete(endpoint) {
        return this.request(endpoint, { method: 'DELETE' });
    }

    static async uploadFile(endpoint, formData) {
        console.log(`[ApiClient] uploadFile called with endpoint: ${endpoint}`);
        console.log('[ApiClient] FormData contents:');
        for (let [key, value] of formData.entries()) {
            console.log(`  ${key}:`, value instanceof File ? `File(${value.name}, ${value.size} bytes)` : value);
        }
        
        // Direct request without AbortController to avoid method confusion
        return this.request(endpoint, {
            method: 'POST',
            body: formData
        });
    }

    static async downloadFile(endpoint, filename) {
        const url = `${API_BASE_URL}${endpoint}`;
        const token = AuthManager.getToken();

        const headers = {
            ...(token && { 'Authorization': `Bearer ${token}` })
        };

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers
            });

            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    // Unauthorized (401) or Forbidden (403) - redirect to login
                    AuthManager.logout();
                    return;
                }
                throw new Error(`HTTP ${response.status}: Failed to download file`);
            }

            // Get the blob from response
            const blob = await response.blob();

            // Get filename from Content-Disposition header if not provided
            if (!filename) {
                const contentDisposition = response.headers.get('Content-Disposition');
                if (contentDisposition) {
                    // Try to match filename="..." or filename=... formats
                    // This regex handles both quoted and unquoted filenames
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (filenameMatch && filenameMatch[1]) {
                        // Remove quotes if present
                        filename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }
            }

            // Fallback to a default filename if still not found
            if (!filename) {
                filename = 'download';
            }

            // Create a download link and trigger it
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the object URL
            setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 100);

        } catch (error) {
            console.error('Download failed:', error);
            throw error;
        }
    }
}

// Utility Functions
function showToast(title, message, type = 'info') {
    const toast = document.createElement('div');
    toast.className = `toast ${type}`;

    // Convert newlines to <br> for multi-line messages (e.g., validation errors)
    const formattedMessage = escapeHtml(message).replace(/\n/g, '<br>');

    toast.innerHTML = `
        <div class="toast-title">${escapeHtml(title)}</div>
        <div class="toast-message">${formattedMessage}</div>
    `;

    elements.toastContainer.appendChild(toast);

    // Show error/validation messages longer (10s) so users can read details
    const duration = type === 'error' ? 10000 : 5000;

    setTimeout(() => {
        toast.remove();
    }, duration);
}

function showLoading(show = true) {
    elements.loadingOverlay.style.display = show ? 'flex' : 'none';
}

function formatFileSize(bytes) {
    // Convert string to number if needed
    const numBytes = parseInt(bytes);
    if (!numBytes || numBytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(numBytes) / Math.log(k));
    return parseFloat((numBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDate(dateString) {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

// Project Functions
async function loadProjects() {
    try {
        elements.projectsList.innerHTML = '<div class="loading">Loading projects...</div>';

        const response = await ApiClient.get('/projects');
        const projects = response.projects || [];

        if (projects.length === 0) {
            elements.projectsList.innerHTML = `
                <div class="no-files">
                    <i class="fas fa-folder-open" style="font-size: 3rem; color: #ddd; margin-bottom: 16px;"></i>
                    <p>No projects yet. Create your first project above!</p>
                </div>
            `;
            return;
        }
        
        elements.projectsList.innerHTML = projects.map(project => `
            <div class="project-card" onclick="openProject(${project.id})">
                <h3>${escapeHtml(project.name)}</h3>
                ${project.description ? `<p>${escapeHtml(project.description)}</p>` : ''}
                <div class="project-meta">
                    <span>Created: ${formatDate(project.created_at)}</span>
                    <span class="files-count" id="files-count-${project.id}">0 files</span>
                </div>
            </div>
        `).join('');
        
        // Load file counts for each project
        projects.forEach(async (project) => {
            try {
                const response = await ApiClient.get(`/projects/${project.id}`);
                const projectDetails = response.project || {};
                // Count only user-uploaded files (exclude generated reports)
                const userFiles = projectDetails.files ? projectDetails.files.filter(file => {
                    const s3Key = file.s3_key || '';
                    return s3Key.includes('/files/');
                }) : [];
                const fileCount = userFiles.length;
                const countElement = document.getElementById(`files-count-${project.id}`);
                if (countElement) {
                    countElement.textContent = `${fileCount} file${fileCount !== 1 ? 's' : ''}`;
                }
            } catch (error) {
                console.error(`Error loading file count for project ${project.id}:`, error);
            }
        });
        
    } catch (error) {
        console.error('Error loading projects:', error);
        elements.projectsList.innerHTML = `
            <div class="error">
                <i class="fas fa-exclamation-triangle" style="color: #dc3545; margin-right: 8px;"></i>
                Failed to load projects. Please try again.
            </div>
        `;
        showToast('Error', 'Failed to load projects', 'error');
    }
}

async function createProject() {
    const name = elements.projectName.value.trim();
    const description = elements.projectDescription.value.trim();

    if (!name) {
        showToast('Error', 'Project name is required', 'error');
        elements.projectName.focus();
        return;
    }

    // Initialize project creation progress bar
    if (!createProjectProgressBar) {
        createProjectProgressBar = new ProgressBar(elements.createProjectProgress, {
            message: 'Creating project...',
            showPercentage: true,
            showSteps: true,
            steps: ['Validating', 'Creating', 'Finalizing', 'Complete']
        });
    }

    createProjectProgressBar.reset();
    createProjectProgressBar.show();
    elements.createProjectBtn.disabled = true;

    try {
        // Step 1: Validating
        createProjectProgressBar.nextStep('Validating project details...');
        await new Promise(resolve => setTimeout(resolve, 500));

        // Step 2: Creating
        createProjectProgressBar.nextStep('Creating project...');
        const project = await ApiClient.post('/projects', {
            name,
            description
        });

        // Step 3: Finalizing
        createProjectProgressBar.nextStep('Finalizing setup...');
        await new Promise(resolve => setTimeout(resolve, 500));

        // Step 4: Complete
        createProjectProgressBar.complete('Project created successfully!');

        showToast('Success', 'Project created successfully', 'success');

        // Clear form
        elements.projectName.value = '';
        elements.projectDescription.value = '';

        // Reload projects
        await loadProjects();

    } catch (error) {
        console.error('Error creating project:', error);
        createProjectProgressBar.updateProgress(0, 'Project creation failed');
        createProjectProgressBar.hide();
        showToast('Error', 'Failed to create project', 'error');
    } finally {
        elements.createProjectBtn.disabled = false;
    }
}

async function openProject(projectId) {
    // Switch to project details view and load project data
    try {
        showLoading(true);

        const response = await ApiClient.get(`/projects/${projectId}`);
        const project = response.project || {};
        currentProject = project;
         
        // Update project details UI
        elements.projectNameTitle.textContent = project.name;
        elements.projectDescriptionContent.textContent = project.description || 'No description provided';

        // Load project files
        await loadProjectFiles(project.files || []);

        // Load project categories and questions
        await loadCategories(projectId);

        // Display existing Excel analysis if available
        displayExistingAnalysis(project);

        // Display existing usability report if available
        displayExistingUsabilityReport(project);

        // Check if any files are still processing and start polling if needed
        const hasProcessingFiles = (project.files || []).some(file =>
            file.processing_status === 'processing' ||
            file.ai_summary === 'Processing in background...'
        );

        if (hasProcessingFiles) {
            startSummaryPolling();
        }

        // Switch to project details view
        switchView('projectDetails');

        // Update URL hash
        window.location.hash = `#project/${projectId}`;
        
    } catch (error) {
        console.error('Error loading project:', error);
        showToast('Error', 'Failed to load project details', 'error');
    } finally {
        showLoading(false);
    }
}


// Admin Functions
function setupAdmin() {
    // Tab switching
    document.querySelectorAll('.tab-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            const tabName = e.target.dataset.tab;
            switchAdminTab(tabName);
        });
    });

    // Add user button
    document.getElementById('addUserBtn')?.addEventListener('click', showAddUserModal);

    // User modal controls
    document.getElementById('closeUserModal')?.addEventListener('click', closeUserModal);
    document.getElementById('cancelUserBtn')?.addEventListener('click', closeUserModal);
    document.getElementById('userForm')?.addEventListener('submit', handleUserFormSubmit);
}

function switchAdminTab(tabName) {
    // Update tab buttons
    document.querySelectorAll('.tab-btn').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.tab === tabName);
    });

    // Update tab content
    document.querySelectorAll('.tab-content').forEach(content => {
        content.classList.toggle('active', content.id === `${tabName}Tab`);
    });

    // Load tab content
    if (tabName === 'users') {
        loadAdminUsers();
    } else if (tabName === 'templates') {
        loadAdminTemplates();
    }
}

// User Management Functions
async function loadAdminUsers() {
    const container = document.getElementById('adminUsersList');

    try {
        container.innerHTML = '<div class="loading">Loading users...</div>';

        // Get the checkbox state
        const showInactive = document.getElementById('showInactiveUsers')?.checked || false;
        const queryParams = showInactive ? '?includeInactive=true' : '';

        const response = await ApiClient.get(`/admin/users${queryParams}`);
        const users = response.users || [];

        if (users.length === 0) {
            container.innerHTML = '<div class="no-files">No users available</div>';
            return;
        }

        const currentUserId = AuthManager.getUser().id;

        container.innerHTML = users.map(user => {
            const isCurrentUser = user.id === currentUserId;
            const statusBadge = user.active
                ? '<span class="badge badge-success">Active</span>'
                : '<span class="badge badge-danger">Inactive</span>';
            const roleBadge = user.role === 'admin'
                ? '<span class="badge badge-primary">Admin</span>'
                : '<span class="badge badge-secondary">User</span>';
            const lastLogin = user.last_login
                ? formatDate(user.last_login)
                : 'Never';

            return `
                <div class="admin-item user-item">
                    <div class="admin-item-content">
                        <div class="admin-item-title">
                            <i class="fas fa-user"></i> ${escapeHtml(user.username)}
                            ${isCurrentUser ? '<span class="badge badge-info">You</span>' : ''}
                        </div>
                        <div class="admin-item-meta">
                            ${escapeHtml(user.email)} •
                            ${roleBadge} •
                            ${statusBadge} •
                            Last login: ${lastLogin}
                        </div>
                        <div class="admin-item-date">
                            Created: ${formatDate(user.created_at)}
                        </div>
                    </div>
                    <div class="admin-item-actions">
                        <button class="btn btn-small btn-outline" onclick="editUser(${user.id})">
                            <i class="fas fa-edit"></i> Edit
                        </button>
                        ${!isCurrentUser ? `
                            <button class="btn btn-small btn-danger" onclick="deleteUser(${user.id}, '${escapeHtml(user.username)}')">
                                <i class="fas fa-trash"></i> Delete
                            </button>
                        ` : ''}
                    </div>
                </div>
            `;
        }).join('');

    } catch (error) {
        console.error('Error loading admin users:', error);
        container.innerHTML = '<div class="error">Failed to load users</div>';
        showToast('Error', 'Failed to load users', 'error');
    }
}

function showAddUserModal() {
    document.getElementById('userModalTitle').textContent = 'Add New User';
    document.getElementById('userId').value = '';
    document.getElementById('userUsername').value = '';
    document.getElementById('userEmail').value = '';
    document.getElementById('userPassword').value = '';
    document.getElementById('userPassword').required = true;
    document.getElementById('passwordGroup').style.display = 'block';
    document.getElementById('userRole').value = 'user';
    document.getElementById('userActive').checked = true;
    document.getElementById('userFormError').style.display = 'none';
    document.getElementById('userModal').style.display = 'block';
}

async function editUser(userId) {
    try {
        // Get all users (including inactive) and find the one we're editing
        const response = await ApiClient.get('/admin/users?includeInactive=true');
        const users = response.users || [];
        const user = users.find(u => u.id === userId);

        if (!user) {
            showToast('Error', 'User not found', 'error');
            return;
        }

        // Populate form
        document.getElementById('userModalTitle').textContent = 'Edit User';
        document.getElementById('userId').value = user.id;
        document.getElementById('userUsername').value = user.username;
        document.getElementById('userEmail').value = user.email;
        document.getElementById('userPassword').value = '';
        document.getElementById('userPassword').required = false;
        document.getElementById('passwordGroup').style.display = 'none'; // Hide password field for edit
        document.getElementById('userRole').value = user.role;
        document.getElementById('userActive').checked = user.active;
        document.getElementById('userFormError').style.display = 'none';
        document.getElementById('userModal').style.display = 'block';

    } catch (error) {
        console.error('Error loading user:', error);
        showToast('Error', 'Failed to load user details', 'error');
    }
}

function closeUserModal() {
    document.getElementById('userModal').style.display = 'none';
    document.getElementById('userForm').reset();
}

async function handleUserFormSubmit(e) {
    e.preventDefault();

    const userId = document.getElementById('userId').value;
    const username = document.getElementById('userUsername').value.trim();
    const email = document.getElementById('userEmail').value.trim();
    const password = document.getElementById('userPassword').value;
    const role = document.getElementById('userRole').value;
    const active = document.getElementById('userActive').checked;

    const errorDiv = document.getElementById('userFormError');
    errorDiv.style.display = 'none';

    // Validation
    if (!username || !email || !role) {
        errorDiv.textContent = 'Please fill in all required fields';
        errorDiv.style.display = 'block';
        return;
    }

    if (username.length < 3 || username.length > 50) {
        errorDiv.textContent = 'Username must be between 3 and 50 characters';
        errorDiv.style.display = 'block';
        return;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
        errorDiv.textContent = 'Username can only contain letters, numbers, hyphens, and underscores';
        errorDiv.style.display = 'block';
        return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
        errorDiv.textContent = 'Please enter a valid email address';
        errorDiv.style.display = 'block';
        return;
    }

    // For new users, password is required
    if (!userId && (!password || password.length < 8)) {
        errorDiv.textContent = 'Password must be at least 8 characters long';
        errorDiv.style.display = 'block';
        return;
    }

    const saveBtn = document.getElementById('saveUserBtn');
    const originalBtnText = saveBtn.innerHTML;
    saveBtn.disabled = true;
    saveBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Saving...';

    try {
        if (userId) {
            // Update existing user
            const updateData = {
                username,
                email,
                role,
                active
            };

            await ApiClient.request(`/admin/users/${userId}`, {
                method: 'PUT',
                body: JSON.stringify(updateData)
            });

            showToast('Success', 'User updated successfully', 'success');
        } else {
            // Create new user
            const createData = {
                username,
                email,
                password,
                role,
                active
            };

            await ApiClient.post('/admin/users', createData);

            showToast('Success', 'User created successfully', 'success');
        }

        closeUserModal();
        loadAdminUsers();

    } catch (error) {
        console.error('Error saving user:', error);
        let errorMessage = error.message || 'Failed to save user';

        // Extract the actual error message from "HTTP 403: message" format
        if (errorMessage.includes('HTTP')) {
            const parts = errorMessage.split(':');
            if (parts.length > 1) {
                // Join all parts after the first colon (in case message contains colons)
                errorMessage = parts.slice(1).join(':').trim();
            }
        }

        errorDiv.textContent = errorMessage;
        errorDiv.style.display = 'block';
    } finally {
        saveBtn.disabled = false;
        saveBtn.innerHTML = originalBtnText;
    }
}

async function deleteUser(userId, username) {
    if (!confirm(`Are you sure you want to delete user "${username}"?\n\nThis will deactivate the user account.`)) {
        return;
    }

    try {
        showLoading(true);

        await ApiClient.delete(`/admin/users/${userId}`);

        showToast('Success', `User "${username}" deleted successfully`, 'success');
        loadAdminUsers();

    } catch (error) {
        console.error('Error deleting user:', error);
        const errorMessage = error.message || 'Failed to delete user';
        showToast('Error', errorMessage.includes('HTTP') ? errorMessage.split(':')[1].trim() : errorMessage, 'error');
    } finally {
        showLoading(false);
    }
}

async function loadAdminTemplates() {
    const container = document.getElementById('adminTemplatesList');

    try {
        container.innerHTML = '<div class="loading">Loading templates...</div>';

        const response = await ApiClient.get('/templates');
        const templates = response.templates || [];

        // Separate templates by type
        const usabilityTemplates = templates.filter(t => t.template_type === 'usability_report');
        const otherTemplates = templates.filter(t => t.template_type !== 'usability_report');

        let html = `
            <!-- Upload Form -->
            <div class="upload-template-section" style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
                <h3 style="margin-top: 0;">Upload Usability Report Template</h3>
                <form id="uploadTemplateForm" style="display: flex; gap: 10px; flex-wrap: wrap; align-items: end;">
                    <div style="flex: 1; min-width: 200px;">
                        <label for="templateName" style="display: block; margin-bottom: 5px; font-weight: 500;">Template Name</label>
                        <input type="text" id="templateName" name="name" required
                               placeholder="e.g., Usability Report Template"
                               style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px;">
                    </div>
                    <div style="flex: 1; min-width: 200px;">
                        <label for="templateFile" style="display: block; margin-bottom: 5px; font-weight: 500;">Select .docx File</label>
                        <input type="file" id="templateFile" name="file"
                               accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                               required
                               style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px;">
                    </div>
                    <button type="submit" class="btn btn-primary" style="margin-bottom: 0;">
                        <i class="fas fa-upload"></i> Upload Template
                    </button>
                </form>
                <small style="display: block; margin-top: 10px; color: #666;">
                    <i class="fas fa-info-circle"></i> Only .docx files are supported. Max file size: 5MB. Template will be validated on upload.
                </small>
            </div>

            <!-- Placeholder Help -->
            <div class="template-help-section" style="background: #e7f3ff; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
                <h4 style="margin-top: 0; color: #0066cc;">
                    <i class="fas fa-question-circle"></i> Template Placeholders Guide
                </h4>
                <p style="margin: 10px 0;">Use these placeholders in your Word document:</p>
                <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
                    <thead>
                        <tr style="background: #d0e7ff;">
                            <th style="padding: 8px; text-align: left; border: 1px solid #b3d9ff;">Placeholder</th>
                            <th style="padding: 8px; text-align: left; border: 1px solid #b3d9ff;">Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{project_name}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Project name</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{generated_date}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Report generation date</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{#questions}...{/questions}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Repeating section for each question</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{question_number}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Question index (1, 2, 3...)</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{question_text}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Question text</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{summary}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Summary of the question</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{#quotes}...{/quotes}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Repeating section for participant quotes</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{participant_name}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Participant label (P1, P2, P3...)</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{participant_quote}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Quote from participant</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{#recommendations}...{/recommendations}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Repeating section for recommendations</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{recommendation_text}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Recommendation text</td></tr>
                    </tbody>
                </table>
                <div style="margin-top: 15px; padding: 12px; background: #fff3cd; border-left: 4px solid #ffc107; border-radius: 4px;">
                    <strong style="color: #856404;">⚠️ Important: Avoid Word Formatting Issues</strong>
                    <ul style="margin: 8px 0 0 20px; color: #856404; font-size: 13px; line-height: 1.6;">
                        <li><strong>Copy placeholders</strong> from this table instead of typing them manually</li>
                        <li>After creating your template, select all text (Ctrl+A) and <strong>clear formatting</strong> (Ctrl+Spacebar, then Ctrl+Q)</li>
                        <li>If you get "duplicate tag" errors, copy text to Notepad and paste back into a new Word document</li>
                        <li>Use <strong>underscores</strong> in placeholders: <code>{participant_name}</code> not <code>{participant name}</code></li>
                    </ul>
                </div>
            </div>
        `;

        // Display Usability Report Templates
        if (usabilityTemplates.length > 0) {
            html += '<h3><i class="fas fa-file-word"></i> Usability Report Templates</h3>';
            html += usabilityTemplates.map(template => `
                <div class="admin-item ${template.is_active ? 'active-template' : ''}" style="border-left: 4px solid ${template.is_active ? '#28a745' : '#ddd'};">
                    <div class="admin-item-content">
                        <div class="admin-item-title">
                            ${escapeHtml(template.name)}
                            ${template.is_active ? '<span class="badge" style="background: #28a745; color: white; padding: 2px 8px; border-radius: 12px; font-size: 12px; margin-left: 8px;">Active</span>' : ''}
                        </div>
                        <div class="admin-item-meta">
                            <span style="color: #0066cc; font-weight: 500;">Usability Report</span> •
                            Size: ${formatFileSize(template.file_size || 0)} •
                            Created: ${formatDate(template.created_at)}
                        </div>
                    </div>
                    <div class="admin-item-actions">
                        ${!template.is_active ? `
                            <button class="btn btn-small btn-primary" onclick="activateTemplate(${template.id})">
                                <i class="fas fa-check"></i> Activate
                            </button>
                        ` : ''}
                        <button class="btn btn-small btn-outline" onclick="downloadTemplate(${template.id}, '${escapeHtml(template.name)}')">
                            <i class="fas fa-download"></i> Download
                        </button>
                        <button class="btn btn-small btn-danger" onclick="deleteTemplate(${template.id})">
                            <i class="fas fa-trash"></i> Delete
                        </button>
                    </div>
                </div>
            `).join('');
        }

        // Display Other Templates (Legacy)
        if (otherTemplates.length > 0) {
            html += '<h3 style="margin-top: 30px;"><i class="fas fa-file-alt"></i> Text Templates (Legacy)</h3>';
            html += otherTemplates.map(template => `
                <div class="admin-item">
                    <div class="admin-item-content">
                        <div class="admin-item-title">${escapeHtml(template.name)}</div>
                        <div class="admin-item-meta">Category: ${escapeHtml(template.category || 'General')} • Created: ${formatDate(template.created_at)}</div>
                    </div>
                    <div class="admin-item-actions">
                        <button class="btn btn-small btn-outline" onclick="viewTemplate('${escapeHtml(template.name)}', '${escapeHtml(template.content)}')">
                            <i class="fas fa-eye"></i> View
                        </button>
                        <button class="btn btn-small btn-danger" onclick="deleteTemplate(${template.id})">
                            <i class="fas fa-trash"></i> Delete
                        </button>
                    </div>
                </div>
            `).join('');
        }

        if (templates.length === 0) {
            html += '<div class="no-files">No templates available</div>';
        }

        container.innerHTML = html;

        // Attach form submit handler
        const form = document.getElementById('uploadTemplateForm');
        if (form) {
            form.addEventListener('submit', handleTemplateUpload);
        }

    } catch (error) {
        console.error('Error loading admin templates:', error);
        container.innerHTML = '<div class="error">Failed to load templates</div>';
    }
}

// Template Upload Handler
async function handleTemplateUpload(event) {
    event.preventDefault();

    const formData = new FormData(event.target);
    const file = formData.get('file');
    const name = formData.get('name');

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.docx')) {
        showToast('Error', 'Only .docx files are allowed', 'error');
        return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
        showToast('Error', 'File size must be less than 5MB', 'error');
        return;
    }

    // Validate name
    if (!name || name.trim().length < 3) {
        showToast('Error', 'Template name must be at least 3 characters', 'error');
        return;
    }

    try {
        showToast('Uploading', 'Uploading and validating template...', 'info');

        const response = await ApiClient.uploadFile('/admin/templates/upload', formData);

        showToast('Success', response.message || 'Template uploaded successfully', 'success');

        // Reload templates
        await loadAdminTemplates();
    } catch (error) {
        console.error('Template upload error:', error);

        // Clean up error message - remove "HTTP 400:" prefix for better UX
        let errorMessage = error.message || 'Failed to upload template';
        const httpPrefixMatch = errorMessage.match(/^HTTP \d+:\s*/);
        if (httpPrefixMatch) {
            errorMessage = errorMessage.substring(httpPrefixMatch[0].length);
        }

        showToast('Error', errorMessage, 'error');
    }
}

// Activate Template
async function activateTemplate(templateId) {
    if (!confirm('Activate this template? This will deactivate the current active template.')) {
        return;
    }

    try {
        const response = await ApiClient.put(`/admin/templates/${templateId}/activate`);
        showToast('Success', response.message || 'Template activated successfully', 'success');
        await loadAdminTemplates();
    } catch (error) {
        console.error('Error activating template:', error);

        // Clean up error message
        let errorMessage = error.message || 'Failed to activate template';
        const httpPrefixMatch = errorMessage.match(/^HTTP \d+:\s*/);
        if (httpPrefixMatch) {
            errorMessage = errorMessage.substring(httpPrefixMatch[0].length);
        }

        showToast('Error', errorMessage, 'error');
    }
}

// Download Template
async function downloadTemplate(templateId, templateName) {
    try {
        // Use ApiClient.downloadFile() method - same as report downloads
        await ApiClient.downloadFile(`/admin/templates/${templateId}/download`, `${templateName}.docx`);
        showToast('Success', 'Template downloaded', 'success');
    } catch (error) {
        console.error('Download error:', error);

        // Clean up error message
        let errorMessage = error.message || 'Failed to download template';
        const httpPrefixMatch = errorMessage.match(/^HTTP \d+:\s*/);
        if (httpPrefixMatch) {
            errorMessage = errorMessage.substring(httpPrefixMatch[0].length);
        }

        showToast('Error', errorMessage, 'error');
    }
}

// Modal Functions
function setupModals() {
    // Admin button - switch to admin view
    elements.adminBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('Admin button clicked, switching to admin view');
        switchView('admin');
        switchAdminTab('users'); // Load users tab by default
        window.location.hash = '#admin';
    });

    // Back to main from admin
    elements.backToMainBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('Back button clicked, switching to main view');
        switchView('main');
        window.location.hash = '';
    });

    // Close modals when clicking outside
    window.addEventListener('click', (e) => {
        if (e.target.classList.contains('modal')) {
            e.target.style.display = 'none';
        }
    });
}

// Utility function to escape HTML
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

// Copy to clipboard function
function copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(() => {
        showToast('Success', 'Copied to clipboard', 'success');
    }).catch(() => {
        showToast('Error', 'Failed to copy to clipboard', 'error');
    });
}

// Format JSON content for human-readable display
function formatSummaryContent(content) {
    if (!content) return '<div class="no-summary">No summary available.</div>';

    // Try to detect and parse JSON
    let data;
    try {
        // Check if content looks like JSON (starts with { or [)
        const trimmedContent = content.trim();
        if (trimmedContent.startsWith('{') || trimmedContent.startsWith('[')) {
            data = JSON.parse(trimmedContent);
        } else {
            // Not JSON, return as formatted text
            return `<div class="summary-text">${escapeHtml(content).replace(/\n/g, '<br>')}</div>`;
        }
    } catch (e) {
        // Not valid JSON, return as formatted text
        return `<div class="summary-text">${escapeHtml(content).replace(/\n/g, '<br>')}</div>`;
    }

    // Format the JSON object in a human-readable way
    function formatValue(value, indent = 0) {
        if (value === null || value === undefined) {
            return '<span class="summary-null">—</span>';
        }

        if (typeof value === 'object' && !Array.isArray(value)) {
            let html = '';

            // Add visual container for nested objects
            if (indent > 0) {
                html += `<div class="summary-object-container" style="margin-left: ${indent * 20}px;">`;
            }

            for (const [key, val] of Object.entries(value)) {
                const formattedKey = key
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase());

                html += `<div class="summary-field">`;
                html += `<strong class="summary-key">${escapeHtml(formattedKey)}:</strong> `;

                if (typeof val === 'object' && val !== null) {
                    html += '<br>' + formatValue(val, indent + 1);
                } else {
                    html += formatValue(val, indent);
                }
                html += '</div>';
            }

            if (indent > 0) {
                html += '</div>';
            }

            return html;
        }

        if (Array.isArray(value)) {
            if (value.length === 0) {
                return '<span class="summary-empty">None</span>';
            }

            let html = '<div class="summary-list">';
            value.forEach((item) => {
                if (typeof item === 'object' && item !== null) {
                    html += `<div class="summary-list-item" style="margin-left: ${(indent + 1) * 20}px; margin-top: 8px;">`;
                    html += formatValue(item, indent + 1);
                    html += '</div>';
                } else {
                    html += `<div class="summary-list-item" style="margin-left: ${(indent + 1) * 20}px;">`;
                    html += `<span class="summary-bullet">•</span> ${escapeHtml(String(item))}`;
                    html += '</div>';
                }
            });
            html += '</div>';
            return html;
        }

        if (typeof value === 'boolean') {
            return `<span class="summary-boolean">${value ? 'Yes' : 'No'}</span>`;
        }

        if (typeof value === 'number') {
            return `<span class="summary-number">${value}</span>`;
        }

        // String value
        return `<span class="summary-value">${escapeHtml(String(value))}</span>`;
    }

    return '<div class="summary-formatted">' + formatValue(data) + '</div>';
}

// View Management Functions
function switchView(viewName) {
    console.log('Switching view to:', viewName);
    console.log('Available views:', Object.keys(views));

    Object.keys(views).forEach(key => {
        const view = views[key];
        if (view) {
            if (key === viewName) {
                view.style.display = 'block';
                console.log(`Showing view: ${key}`);
            } else {
                view.style.display = 'none';
            }
        } else {
            console.warn(`View element not found: ${key}`);
        }
    });
    currentView = viewName;
}

function goBackToMain() {
    stopSummaryPolling(); // Stop polling when leaving project
    switchView('main');
    window.location.hash = '';
    currentProject = null;
}

function goBackToProject() {
    if (currentProject) {
        switchView('projectDetails');
        window.location.hash = `#project/${currentProject.id}`;
    } else {
        goBackToMain();
    }
}

// Project Details Functions
function loadProjectFiles(files) {
    // Filter out generated reports (Excel Analysis and Usability Reports)
    // These are displayed separately in their own sections
    const userUploadedFiles = files.filter(file => {
        const s3Key = file.s3_key || '';

        // Only include files stored in the /files/ directory
        // Generated reports are stored in /excel/ and /usability-reports/ directories
        return s3Key.includes('/files/');
    });

    elements.filesCount.textContent = `${userUploadedFiles.length} file${userUploadedFiles.length !== 1 ? 's' : ''}`;

    if (userUploadedFiles.length === 0) {
        elements.filesList.innerHTML = '<div class="no-files">No files uploaded yet</div>';
        elements.generateExcelAnalysisBtn.disabled = true;
        elements.generateUsabilityReportBtn.disabled = true;
        return;
    }

    // Store file summaries in a global object for easy access
    window.fileSummaries = {};

    elements.filesList.innerHTML = userUploadedFiles.map(file => {
        // Store the full summary
        window.fileSummaries[file.id] = {
            name: file.original_name,
            summary: file.ai_summary || 'No summary available'
        };
        
        return `
            <div class="file-item" data-file-id="${file.id}">
                <div class="file-info">
                    <div class="file-header">
                        <div class="participant-label-container" onclick="editParticipantLabel(${file.id}, '${escapeHtml(file.participant_label || '')}')">
                            <span class="participant-label editable" data-file-id="${file.id}" data-current-label="${escapeHtml(file.participant_label || '')}">
                                ${file.participant_label || 'No label'}
                            </span>
                            <i class="fas fa-edit participant-edit-icon"></i>
                        </div>
                        <div class="file-name">
                            <i class="fas fa-file"></i>
                            ${escapeHtml(file.original_name)}
                        </div>
                    </div>
                    <div class="file-meta">
                        ${formatFileSize(file.file_size)} • Uploaded ${formatDate(file.upload_date)}
                    </div>
                    ${file.ai_summary ? `
                        <div class="file-summary">
                            <strong>Summary:</strong> ${escapeHtml(file.ai_summary.substring(0, 150))}${file.ai_summary.length > 150 ? '...' : ''}
                        </div>
                    ` : ''}
                </div>
                <div class="file-actions">
                    <button class="btn btn-small btn-outline" onclick="viewFileSummary(${file.id})">
                        <i class="fas fa-eye"></i> View Summary
                    </button>
                    <button class="btn btn-small btn-danger" onclick="deleteFile(${file.id})">
                        <i class="fas fa-trash"></i> Delete
                    </button>
                </div>
            </div>
        `;
    }).join('');

    elements.generateExcelAnalysisBtn.disabled = false;
    elements.generateUsabilityReportBtn.disabled = false;
}

// File Upload Functions
function setupFileUpload() {
    // Click to upload
    elements.uploadArea?.addEventListener('click', () => {
        elements.fileInput.click();
    });
    
    // File input change
    elements.fileInput?.addEventListener('change', (e) => {
        handleFileUpload(Array.from(e.target.files));
    });
    
    // Drag and drop
    elements.uploadArea?.addEventListener('dragover', (e) => {
        e.preventDefault();
        elements.uploadArea.classList.add('drag-over');
    });
    
    elements.uploadArea?.addEventListener('dragleave', (e) => {
        e.preventDefault();
        elements.uploadArea.classList.remove('drag-over');
    });
    
    elements.uploadArea?.addEventListener('drop', (e) => {
        e.preventDefault();
        elements.uploadArea.classList.remove('drag-over');
        
        const files = Array.from(e.dataTransfer.files);
        handleFileUpload(files);
    });
}

// Progress bar instances
let uploadProgressBar = null;
let reportProgressBar = null;
let createProjectProgressBar = null;

async function handleFileUpload(files) {
    if (!currentProject || files.length === 0) {
        return;
    }

    // Initialize upload progress bar
    if (!uploadProgressBar) {
        uploadProgressBar = new ProgressBar(elements.uploadProgress, {
            message: 'Preparing upload...',
            showPercentage: true,
            showSteps: true,
            steps: ['Upload', 'Processing', 'Complete']
        });
    }

    uploadProgressBar.reset();
    uploadProgressBar.show();

    try {
        // Step 1: Upload files
        uploadProgressBar.nextStep('Uploading files...');

        const uploadedFiles = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            // Update progress for current file
            const fileProgress = ((i / files.length) * 25); // 0-25% for upload phase
            uploadProgressBar.updateProgress(fileProgress, `Uploading ${file.name} (${i + 1}/${files.length})`);

            // Create form data
            const formData = new FormData();
            formData.append('file', file);

            // Upload file using direct fetch with timeout and better error handling
            const url = `${API_BASE_URL}/projects/${currentProject.id}/upload`;
            const token = AuthManager.getToken();

            console.log(`[Upload] Uploading file ${i + 1}/${files.length}: ${file.name}`);

            // Create an AbortController for timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout

            try {
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData,
                    signal: controller.signal
                });

                clearTimeout(timeoutId);

                console.log(`[Upload] Response status: ${response.status}`);

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error(`[Upload] Error response:`, errorText);
                    throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
                }

                const result = await response.json();
                console.log(`[Upload] File uploaded successfully:`, result);
                uploadedFiles.push(result.file);
            } catch (fetchError) {
                clearTimeout(timeoutId);

                if (fetchError.name === 'AbortError') {
                    console.error(`[Upload] Request timed out after 2 minutes`);
                    throw new Error(`Upload timed out. The file may be too large or the server is not responding.`);
                }

                console.error(`[Upload] Fetch error:`, fetchError);
                throw fetchError;
            }
        }

        // Check if files are being processed asynchronously (status 202)
        const isAsyncProcessing = uploadedFiles.some(f => f.processing_status === 'processing');

        if (isAsyncProcessing) {
            // Step 2: Processing (async AI extraction and summarization)
            uploadProgressBar.nextStep('Processing files with AI...');

            // Poll for file processing status
            await pollFileProcessingStatus(uploadedFiles, uploadProgressBar);

            // Reload project to show updated files
            await openProject(currentProject.id);

            // Start polling for summary updates (in case processing is still ongoing)
            startSummaryPolling();

            showToast('Success', `${files.length} file${files.length !== 1 ? 's' : ''} processed successfully!`, 'success');

        } else {
            // Legacy synchronous processing - files already complete
            uploadProgressBar.nextStep('Processing complete...');
            await new Promise(resolve => setTimeout(resolve, 500));

            uploadProgressBar.complete(`${files.length} file${files.length !== 1 ? 's' : ''} uploaded successfully!`);

            showToast('Success', `${files.length} file${files.length !== 1 ? 's' : ''} uploaded successfully`, 'success');

            // Reload project to show new files
            await openProject(currentProject.id);
        }

    } catch (error) {
        console.error('Error uploading files:', error);
        uploadProgressBar.updateProgress(0, 'Upload failed');
        uploadProgressBar.hide();
        showToast('Error', 'Failed to upload files', 'error');
    } finally {
        elements.fileInput.value = '';
    }
}

/**
 * Poll file processing status for async Step Functions workflow
 * Updates progress bar based on processing status
 */
async function pollFileProcessingStatus(files, progressBar) {
    const maxAttempts = 120; // 2 minutes max (120 * 1 second)
    let attempt = 0;
    let delay = 1000; // Start with 1 second
    const maxDelay = 5000; // Max 5 seconds between polls

    const fileIds = files.filter(f => f.processing_status === 'processing').map(f => f.id);

    while (attempt < maxAttempts && fileIds.length > 0) {
        attempt++;

        // Poll each file's status
        const statusChecks = await Promise.all(
            fileIds.map(async (fileId) => {
                try {
                    const response = await ApiClient.get(`/files/${fileId}/status`);
                    return response;
                } catch (error) {
                    console.error(`Error checking status for file ${fileId}:`, error);
                    return null;
                }
            })
        );

        // Check statuses
        let allCompleted = true;
        let anyFailed = false;
        let anyNeedingReview = false;
        let totalQualityScore = 0;
        let completedCount = 0;

        for (const status of statusChecks) {
            if (!status) continue;

            if (status.status === 'completed') {
                completedCount++;
                totalQualityScore += status.qualityScore || 0;
            } else if (status.status === 'processing') {
                allCompleted = false;
                // Update progress bar with processing message
                progressBar.updateProgress(50, 'Processing with AI (may take 30-60 seconds)...');
            } else if (status.status === 'failed') {
                anyFailed = true;
            } else if (status.status === 'pending_review') {
                anyNeedingReview = true;
            }
        }

        if (allCompleted) {
            // All files completed successfully
            const avgQualityScore = completedCount > 0 ? Math.round(totalQualityScore / completedCount) : 0;

            // Step 3: Complete
            progressBar.complete(`Processing complete! Quality score: ${avgQualityScore}/100`);
            return;
        }

        if (anyFailed) {
            progressBar.updateProgress(0, 'Processing failed');
            progressBar.hide();
            throw new Error('File processing failed');
        }

        if (anyNeedingReview) {
            progressBar.nextStep('Requires human review...');
            await new Promise(resolve => setTimeout(resolve, 2000));
            progressBar.complete('Queued for human review');
            return;
        }

        // Wait before next poll (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, delay));
        delay = Math.min(delay * 1.2, maxDelay); // Increase delay up to max
    }

    // Timeout reached
    if (attempt >= maxAttempts) {
        progressBar.updateProgress(0, 'Processing timeout - please refresh to check status');
        await new Promise(resolve => setTimeout(resolve, 3000));
        progressBar.hide();
    }
}

// Summary Generation Functions
function displayExistingAnalysis(project) {
    if (project.excel_analysis_filename && project.excel_analysis_generated_at) {
        // Show the existing analysis section
        elements.existingExcelAnalysis.style.display = 'block';

        // Update the filename
        elements.existingExcelAnalysis.querySelector('.analysis-filename').textContent = project.excel_analysis_filename;

        // Format and display the generation date
        const generatedDate = new Date(project.excel_analysis_generated_at);
        const formattedDate = generatedDate.toLocaleString();
        elements.existingExcelAnalysis.querySelector('.analysis-date').textContent = `Generated: ${formattedDate}`;

        // Update the generate button text
        elements.generateExcelAnalysisBtn.innerHTML = '<i class="fas fa-sync"></i> Regenerate Excel Analysis';

    } else {
        // Hide the existing analysis section
        elements.existingExcelAnalysis.style.display = 'none';

        // Reset the generate button text
        elements.generateExcelAnalysisBtn.innerHTML = '<i class="fas fa-magic"></i> Generate Excel Analysis';
    }
}

async function downloadExistingAnalysis() {
    if (!currentProject) return;

    try {
        // Use the downloadFile method to handle blob response
        await ApiClient.downloadFile(`/projects/${currentProject.id}/download-excel-analysis`);
        showToast('Success', 'Excel analysis downloaded successfully', 'success');
    } catch (error) {
        console.error('Error downloading existing analysis:', error);
        showToast('Error', 'Failed to download analysis file', 'error');
    }
}

function displayExistingUsabilityReport(project) {
    if (project.usability_report_filename && project.usability_report_generated_at) {
        // Show the existing report section
        elements.existingUsabilityReport.style.display = 'block';

        // Update the filename
        elements.existingUsabilityReport.querySelector('.report-filename').textContent = project.usability_report_filename;

        // Format and display the generation date
        const generatedDate = new Date(project.usability_report_generated_at);
        const formattedDate = generatedDate.toLocaleString();
        elements.existingUsabilityReport.querySelector('.report-date').textContent = `Generated: ${formattedDate}`;

        // Update the generate button text
        elements.generateUsabilityReportBtn.innerHTML = '<i class="fas fa-sync"></i> Regenerate Usability Report';

    } else {
        // Hide the existing report section
        elements.existingUsabilityReport.style.display = 'none';

        // Reset the generate button text
        elements.generateUsabilityReportBtn.innerHTML = '<i class="fas fa-file-alt"></i> Generate Usability Report';
    }
}

async function downloadExistingReport() {
    if (!currentProject) return;

    try {
        // Use the downloadFile method to handle blob response
        await ApiClient.downloadFile(`/projects/${currentProject.id}/download-usability-report`);
        showToast('Success', 'Usability report downloaded successfully', 'success');
    } catch (error) {
        console.error('Error downloading existing report:', error);
        showToast('Error', 'Failed to download report file', 'error');
    }
}

// Usability report progress bar instance
let usabilityProgressBar = null;

async function generateUsabilityReport() {
    if (!currentProject) return;

    // Initialize usability progress bar
    if (!usabilityProgressBar) {
        usabilityProgressBar = new ProgressBar(elements.reportProgress, {
            message: 'Preparing report...',
            showPercentage: true,
            showSteps: true,
            steps: ['Initializing', 'Generating content', 'Quality check', 'Complete']
        });
    }

    usabilityProgressBar.reset();
    usabilityProgressBar.show();
    elements.generateUsabilityReportBtn.disabled = true;

    try {
        // Step 1: Initialize async report generation
        usabilityProgressBar.nextStep('Initializing report generation...');
        const response = await ApiClient.post(`/projects/${currentProject.id}/generate-usability-report-async`, {});

        console.log('Report generation initiated:', response);

        // Step 2: Poll for completion
        usabilityProgressBar.nextStep('Generating content with AI (may take 2-3 minutes)...');
        await pollReportGenerationStatus(currentProject.id, usabilityProgressBar);

        // Reload project to get updated report info
        await openProject(currentProject.id);

        showToast('Success', 'Usability report generated successfully', 'success');

    } catch (error) {
        console.error('Error generating usability report:', error);
        usabilityProgressBar.updateProgress(0, 'Report generation failed');
        usabilityProgressBar.hide();
        showToast('Error', 'Failed to generate usability report', 'error');
    } finally {
        elements.generateUsabilityReportBtn.disabled = false;
    }
}

/**
 * Poll report generation status until complete, failed, or timeout
 */
async function pollReportGenerationStatus(projectId, progressBar) {
    const maxAttempts = 180; // 3 minutes max (180 * 1 second)
    let attempt = 0;
    let delay = 2000; // Start with 2 seconds (report generation is slower)
    const maxDelay = 5000; // Max 5 seconds between polls

    while (attempt < maxAttempts) {
        attempt++;

        try {
            const status = await ApiClient.get(`/projects/${projectId}/report-generation-status`);

            console.log(`Report status poll ${attempt}:`, status);

            // Check status
            if (status.status === 'completed') {
                // Step 3: Quality check complete
                progressBar.nextStep(`Quality check complete (Score: ${status.qualityScore || 'N/A'})...`);
                await new Promise(resolve => setTimeout(resolve, 500));

                // Step 4: Complete
                progressBar.complete(`Report complete! Quality score: ${status.qualityScore || 'N/A'}/100`);
                return;
            } else if (status.status === 'processing') {
                // Update progress bar with iteration info
                const iterationMsg = status.iterations > 0
                    ? ` (Iteration ${status.iterations}/3)`
                    : '';
                progressBar.updateProgress(50, `Generating content with AI${iterationMsg}...`);
            } else if (status.status === 'failed') {
                progressBar.updateProgress(0, 'Report generation failed');
                progressBar.hide();
                throw new Error('Report generation failed');
            } else if (status.status === 'pending_review') {
                progressBar.nextStep('Requires human review...');
                await new Promise(resolve => setTimeout(resolve, 2000));
                progressBar.complete('Queued for human review');
                return;
            }

            // Wait before next poll (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, delay));
            delay = Math.min(delay * 1.1, maxDelay); // Gradual increase

        } catch (error) {
            console.error(`Error checking report status (attempt ${attempt}):`, error);

            // If it's a 404 or network error, continue polling
            if (attempt < maxAttempts - 10) {
                await new Promise(resolve => setTimeout(resolve, delay));
                continue;
            } else {
                throw error; // Throw on last few attempts
            }
        }
    }

    // Timeout reached
    progressBar.updateProgress(0, 'Report generation timeout - please check status later');
    await new Promise(resolve => setTimeout(resolve, 3000));
    progressBar.hide();
    throw new Error('Report generation timeout');
}

async function generateExcelAnalysis() {
    if (!currentProject) return;

    // Initialize analysis progress bar
    if (!reportProgressBar) {
        reportProgressBar = new ProgressBar(elements.summaryProgress, {
            message: 'Preparing analysis...',
            showPercentage: true,
            showSteps: true,
            steps: ['Analyzing files', 'Generating Excel', 'Finalizing', 'Complete']
        });
    }

    reportProgressBar.reset();
    reportProgressBar.show();
    elements.generateExcelAnalysisBtn.disabled = true;
    elements.summaryResult.style.display = 'none';

    try {
        // Step 1: Analyzing files
        reportProgressBar.nextStep('Analyzing project files...');
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Step 2: Generating Excel
        reportProgressBar.nextStep('Generating Excel analysis...');
        const response = await ApiClient.post(`/projects/${currentProject.id}/generate-excel`, {});

        // Step 3: Finalizing
        reportProgressBar.nextStep('Finalizing analysis...');
        await new Promise(resolve => setTimeout(resolve, 500));

        // Step 4: Complete
        reportProgressBar.complete('Excel analysis generated successfully!');

        // Reload project to get updated analysis info
        await openProject(currentProject.id);

        showToast('Success', 'Excel analysis generated successfully', 'success');

    } catch (error) {
        console.error('Error generating analysis:', error);
        reportProgressBar.updateProgress(0, 'Analysis failed');
        reportProgressBar.hide();
        showToast('Error', 'Failed to generate analysis', 'error');
    } finally {
        elements.generateExcelAnalysisBtn.disabled = false;
    }
}

// Hash-based routing
function handleHashChange() {
    const hash = window.location.hash;

    if (hash === '#admin') {
        // Admin panel URL: #admin
        if (currentView !== 'admin') {
            switchView('admin');
            switchAdminTab('users'); // Load users tab by default
        }
    } else if (hash.startsWith('#project/')) {
        const hashParts = hash.split('/');
        const projectId = parseInt(hashParts[1]);

        if (hashParts.length >= 4 && hashParts[2] === 'file') {
            // File summary URL: #project/123/file/456
            const fileId = parseInt(hashParts[3]);

            // Ensure project is loaded first
            if (!currentProject || currentProject.id !== projectId) {
                // Load project first, then show file summary
                openProject(projectId).then(() => {
                    if (window.fileSummaries?.[fileId]) {
                        viewFileSummary(fileId);
                    }
                });
            } else if (window.fileSummaries?.[fileId]) {
                viewFileSummary(fileId);
            }
        } else {
            // Project details URL: #project/123
            if (projectId && projectId !== currentProject?.id) {
                openProject(projectId);
            } else if (currentView !== 'projectDetails') {
                switchView('projectDetails');
            }
        }
    } else {
        // No hash or unrecognized hash - go to main
        if (currentView !== 'main') {
            goBackToMain();
        }
    }
}

// ==================== CATEGORY & QUESTION MANAGEMENT ====================

// Load categories for a project
async function loadCategories(projectId) {
    try {
        const response = await ApiClient.get(`/projects/${projectId}/categories`);
        const categories = response.categories || [];
        displayCategories(categories);
    } catch (error) {
        console.error('Error loading categories:', error);
        elements.categoriesList.innerHTML = '<div class="no-categories"><i class="fas fa-folder-open"></i><p>Failed to load categories</p></div>';
    }
}

// Display categories in the UI
function displayCategories(categories) {
    if (!categories || categories.length === 0) {
        elements.categoriesList.innerHTML = `
            <div class="no-categories">
                <i class="fas fa-folder-open"></i>
                <p>No categories yet. Click "Add Category" to create one.</p>
            </div>
        `;
        return;
    }

    elements.categoriesList.innerHTML = categories.map(category => `
        <div class="category-item" data-category-id="${category.id}">
            <div class="category-header">
                <div class="category-info">
                    <div class="category-name">
                        <span class="category-order-badge" title="Drag to reorder">${category.display_order}</span>
                        ${escapeHtml(category.name)}
                    </div>
                    ${category.description ? `<div class="category-description">${escapeHtml(category.description)}</div>` : ''}
                </div>
                <div class="category-actions">
                    <button class="btn btn-icon btn-outline" onclick="editCategory(${category.id})" title="Edit category">
                        <i class="fas fa-edit"></i>
                    </button>
                    <button class="btn btn-icon btn-outline" onclick="deleteCategory(${category.id})" title="Delete category">
                        <i class="fas fa-trash"></i>
                    </button>
                </div>
            </div>
            <div class="category-questions" id="questions-${category.id}">
                <div class="loading">Loading questions...</div>
            </div>
        </div>
    `).join('');

    // Load questions for each category
    categories.forEach(category => {
        loadCategoryQuestions(category.id);
    });
}

// Load questions for a specific category
async function loadCategoryQuestions(categoryId) {
    try {
        const response = await ApiClient.get(`/categories/${categoryId}/questions`);
        const questions = response.questions || [];
        displayCategoryQuestions(categoryId, questions);
    } catch (error) {
        console.error('Error loading questions:', error);
        document.getElementById(`questions-${categoryId}`).innerHTML = '<div class="no-questions">Failed to load questions</div>';
    }
}

// Display questions for a category
function displayCategoryQuestions(categoryId, questions) {
    const container = document.getElementById(`questions-${categoryId}`);

    if (!questions || questions.length === 0) {
        container.innerHTML = `
            <div class="category-questions-header">
                <h5>Questions (0)</h5>
                <button class="btn btn-small btn-primary" onclick="addQuestionToCategory(${categoryId})">
                    <i class="fas fa-plus"></i> Add Question
                </button>
            </div>
            <div class="no-questions">No questions in this category</div>
        `;
        return;
    }

    container.innerHTML = `
        <div class="category-questions-header">
            <h5>Questions (${questions.length})</h5>
            <button class="btn btn-small btn-primary" onclick="addQuestionToCategory(${categoryId})">
                <i class="fas fa-plus"></i> Add Question
            </button>
        </div>
        ${questions.map(q => `
            <div class="question-item">
                <div class="question-text">${escapeHtml(q.question)}</div>
                <div class="question-actions">
                    <button class="btn btn-icon btn-outline" onclick="editQuestion(${q.id}, ${categoryId})" title="Edit question">
                        <i class="fas fa-edit"></i>
                    </button>
                    <button class="btn btn-icon btn-outline" onclick="deleteQuestionFromCategory(${q.id})" title="Delete question">
                        <i class="fas fa-trash"></i>
                    </button>
                </div>
            </div>
        `).join('')}
    `;
}

// Show add category modal
function showAddCategoryModal() {
    elements.categoryModalTitle.textContent = 'Add New Category';
    elements.categoryId.value = '';
    elements.categoryProjectId.value = currentProject.id;
    elements.categoryName.value = '';
    elements.categoryDescription.value = '';
    elements.categoryDisplayOrder.value = '0';
    elements.categoryFormError.style.display = 'none';
    elements.categoryModal.style.display = 'block';
}

// Edit category
async function editCategory(categoryId) {
    try {
        // Get all categories to find the one we're editing
        const response = await ApiClient.get(`/projects/${currentProject.id}/categories`);
        const categories = response.categories || [];
        const category = categories.find(c => c.id === categoryId);

        if (!category) {
            showToast('Error', 'Category not found', 'error');
            return;
        }

        elements.categoryModalTitle.textContent = 'Edit Category';
        elements.categoryId.value = category.id;
        elements.categoryProjectId.value = category.project_id;
        elements.categoryName.value = category.name;
        elements.categoryDescription.value = category.description || '';
        elements.categoryDisplayOrder.value = category.display_order || 0;
        elements.categoryFormError.style.display = 'none';
        elements.categoryModal.style.display = 'block';
    } catch (error) {
        console.error('Error loading category:', error);
        showToast('Error', 'Failed to load category details', 'error');
    }
}

// Delete category
async function deleteCategory(categoryId) {
    if (!confirm('Are you sure you want to delete this category? All questions in this category will also be deleted.')) {
        return;
    }

    try {
        await ApiClient.delete(`/categories/${categoryId}`);
        showToast('Success', 'Category deleted successfully', 'success');
        await loadCategories(currentProject.id);
    } catch (error) {
        console.error('Error deleting category:', error);
        showToast('Error', 'Failed to delete category', 'error');
    }
}

// Handle category form submission
async function handleCategoryFormSubmit(e) {
    e.preventDefault();

    const categoryId = elements.categoryId.value;
    const projectId = elements.categoryProjectId.value;
    const name = elements.categoryName.value.trim();
    const description = elements.categoryDescription.value.trim();
    const displayOrder = parseInt(elements.categoryDisplayOrder.value) || 0;

    if (!name) {
        elements.categoryFormError.textContent = 'Category name is required';
        elements.categoryFormError.style.display = 'block';
        return;
    }

    try {
        if (categoryId) {
            // Update existing category
            await ApiClient.put(`/categories/${categoryId}`, {
                name,
                description,
                display_order: displayOrder
            });
            showToast('Success', 'Category updated successfully', 'success');
        } else {
            // Create new category
            await ApiClient.post(`/projects/${projectId}/categories`, {
                name,
                description,
                display_order: displayOrder
            });
            showToast('Success', 'Category created successfully', 'success');
        }

        elements.categoryModal.style.display = 'none';
        await loadCategories(currentProject.id);
    } catch (error) {
        console.error('Error saving category:', error);
        elements.categoryFormError.textContent = error.message || 'Failed to save category';
        elements.categoryFormError.style.display = 'block';
    }
}

// Add question to category
function addQuestionToCategory(categoryId) {
    elements.categoryQuestionModalTitle.textContent = 'Add Question';
    elements.questionId.value = '';
    elements.questionCategoryId.value = categoryId;
    elements.questionText.value = '';
    elements.questionFormError.style.display = 'none';
    elements.categoryQuestionModal.style.display = 'block';
}

// Edit question
async function editQuestion(questionId, categoryId) {
    try {
        const response = await ApiClient.get(`/categories/${categoryId}/questions`);
        const questions = response.questions || [];
        const question = questions.find(q => q.id === questionId);

        if (!question) {
            showToast('Error', 'Question not found', 'error');
            return;
        }

        elements.categoryQuestionModalTitle.textContent = 'Edit Question';
        elements.questionId.value = question.id;
        elements.questionCategoryId.value = categoryId;
        elements.questionText.value = question.question;
        elements.questionFormError.style.display = 'none';
        elements.categoryQuestionModal.style.display = 'block';
    } catch (error) {
        console.error('Error loading question:', error);
        showToast('Error', 'Failed to load question details', 'error');
    }
}

// Delete question from category
async function deleteQuestionFromCategory(questionId) {
    if (!confirm('Are you sure you want to delete this question?')) {
        return;
    }

    try {
        await ApiClient.delete(`/questions/${questionId}`);
        showToast('Success', 'Question deleted successfully', 'success');
        await loadCategories(currentProject.id);
    } catch (error) {
        console.error('Error deleting question:', error);
        showToast('Error', 'Failed to delete question', 'error');
    }
}

// Handle question form submission
async function handleQuestionFormSubmit(e) {
    e.preventDefault();

    const questionId = elements.questionId.value;
    const categoryId = elements.questionCategoryId.value;
    const questionText = elements.questionText.value.trim();

    if (!questionText) {
        elements.questionFormError.textContent = 'Question text is required';
        elements.questionFormError.style.display = 'block';
        return;
    }

    try {
        if (questionId) {
            // Update existing question
            await ApiClient.put(`/questions/${questionId}`, {
                question: questionText
            });
            showToast('Success', 'Question updated successfully', 'success');
        } else {
            // Create new question
            await ApiClient.post(`/categories/${categoryId}/questions`, {
                question: questionText
            });
            showToast('Success', 'Question created successfully', 'success');
        }

        elements.categoryQuestionModal.style.display = 'none';
        await loadCategories(currentProject.id);
    } catch (error) {
        console.error('Error saving question:', error);
        elements.questionFormError.textContent = error.message || 'Failed to save question';
        elements.questionFormError.style.display = 'block';
    }
}

// Close category modal
function closeCategoryModal() {
    elements.categoryModal.style.display = 'none';
}

// Close question modal
function closeCategoryQuestionModal() {
    elements.categoryQuestionModal.style.display = 'none';
}

// Make functions globally available
window.editCategory = editCategory;
window.deleteCategory = deleteCategory;
window.addQuestionToCategory = addQuestionToCategory;
window.editQuestion = editQuestion;
window.deleteQuestionFromCategory = deleteQuestionFromCategory;

// Initialize app
document.addEventListener('DOMContentLoaded', async () => {
    try {
        // Check authentication status first
        if (AuthManager.isAuthenticated()) {
            showAppView();
        } else {
            showLoginView();
        }

        // Setup authentication event listeners
        elements.loginForm?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const email = elements.loginEmail.value.trim();
            const password = elements.loginPassword.value;

            if (!email || !password) {
                showLoginError('Please enter both email and password');
                return;
            }

            elements.loginBtn.disabled = true;
            elements.loginBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Signing In...';
            hideLoginError();

            try {
                await AuthManager.login(email, password);
                showAppView();
                showToast('Success', 'Logged in successfully', 'success');
            } catch (error) {
                showLoginError(error.message);
            } finally {
                elements.loginBtn.disabled = false;
                elements.loginBtn.innerHTML = '<i class="fas fa-sign-in-alt"></i> Sign In';
            }
        });

        elements.logoutBtn?.addEventListener('click', () => {
            AuthManager.logout();
            showToast('Info', 'Logged out successfully', 'info');
        });

        // Setup event listeners
        elements.createProjectBtn?.addEventListener('click', createProject);
        elements.refreshProjectsBtn?.addEventListener('click', loadProjects);
        elements.backBtn?.addEventListener('click', goBackToMain);
        elements.backToProjectBtn?.addEventListener('click', goBackToProject);
        elements.generateExcelAnalysisBtn?.addEventListener('click', generateExcelAnalysis);
        elements.downloadExistingAnalysisBtn?.addEventListener('click', downloadExistingAnalysis);
        elements.generateUsabilityReportBtn?.addEventListener('click', generateUsabilityReport);
        elements.downloadExistingReportBtn?.addEventListener('click', downloadExistingReport);

        // Category management event listeners
        elements.addCategoryBtn?.addEventListener('click', showAddCategoryModal);
        elements.categoryForm?.addEventListener('submit', handleCategoryFormSubmit);
        elements.cancelCategoryBtn?.addEventListener('click', closeCategoryModal);
        elements.closeCategoryModal?.addEventListener('click', closeCategoryModal);

        // Question management event listeners
        elements.categoryQuestionForm?.addEventListener('submit', handleQuestionFormSubmit);
        elements.cancelQuestionFormBtn?.addEventListener('click', closeCategoryQuestionModal);
        elements.closeCategoryQuestionModal?.addEventListener('click', closeCategoryQuestionModal);

        // File summary copy button
        elements.copySummaryTextBtn?.addEventListener('click', () => {
            if (window.currentFileSummary) {
                copyToClipboard(window.currentFileSummary);
            }
        });
        
        // Setup copy summary button
        document.getElementById('copySummaryBtn')?.addEventListener('click', () => {
            const summaryContent = elements.summaryResult.querySelector('.summary-content').textContent;
            copyToClipboard(summaryContent);
        });
        
        // Enter key on project name input
        elements.projectName?.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                createProject();
            }
        });
        
        setupFileUpload();
        setupModals();
        setupAdmin();
        
        // Setup hash-based routing
        window.addEventListener('hashchange', handleHashChange);

        // Handle initial hash only if authenticated
        if (AuthManager.isAuthenticated()) {
            handleHashChange();
        }
        
    } catch (error) {
        console.error('Error initializing app:', error);
        showToast('Error', 'Failed to initialize application', 'error');
    }
});

// Summary polling functionality
let summaryPollingInterval = null;

function startSummaryPolling() {
    // Clear any existing polling
    if (summaryPollingInterval) {
        clearInterval(summaryPollingInterval);
    }

    // Poll every 3 seconds for summary updates
    summaryPollingInterval = setInterval(async () => {
        if (!currentProject) {
            clearInterval(summaryPollingInterval);
            return;
        }

        try {
            const response = await ApiClient.get(`/projects/${currentProject.id}`);
            const project = response.project || {};
            const files = project.files || [];

            // Check if any files have updated summaries or changed processing status
            const hasUpdatedSummaries = files.some(file => {
                const previousSummary = window.fileSummaries?.[file.id]?.summary;
                const currentSummary = file.ai_summary;

                // File has been processed and summary changed
                return (
                    file.processing_status === 'completed' &&
                    currentSummary &&
                    currentSummary !== 'Processing in background...' &&
                    currentSummary !== 'No content to analyze' &&
                    currentSummary !== previousSummary
                );
            });

            if (hasUpdatedSummaries) {
                console.log('[Polling] Detected updated summaries, refreshing file list');

                // Update the file list display with new summaries
                loadProjectFiles(files);

                // Check if all files are done processing
                const stillProcessing = files.some(file =>
                    file.processing_status === 'processing' ||
                    file.ai_summary === 'Processing in background...'
                );

                if (!stillProcessing) {
                    console.log('[Polling] All files processed, stopping polling');
                    // All summaries complete, stop polling
                    clearInterval(summaryPollingInterval);
                    summaryPollingInterval = null;

                    // Show success notification
                    showToast('Success', 'All files have been processed!', 'success');
                }
            }
        } catch (error) {
            console.error('Error polling for summary updates:', error);
            // Continue polling despite errors
        }
    }, 10000); // Poll every 10 seconds

    // Stop polling after 5 minutes to prevent infinite polling
    setTimeout(() => {
        if (summaryPollingInterval) {
            clearInterval(summaryPollingInterval);
            summaryPollingInterval = null;
        }
    }, 300000); // 5 minutes
}

function stopSummaryPolling() {
    if (summaryPollingInterval) {
        clearInterval(summaryPollingInterval);
        summaryPollingInterval = null;
    }
}

// Global functions for onclick handlers
window.openProject = openProject;
window.deleteFile = async (fileId) => {
    if (!confirm('Are you sure you want to delete this file?')) return;

    try {
        await ApiClient.delete(`/files/${fileId}`);
        showToast('Success', 'File deleted successfully', 'success');

        // Reload project to update file list
        await openProject(currentProject.id);

    } catch (error) {
        console.error('Error deleting file:', error);
        showToast('Error', 'Failed to delete file', 'error');
    }
};

/**
 * Edit participant label inline
 * @param {number} fileId - File ID
 * @param {string} currentLabel - Current label value
 */
window.editParticipantLabel = async (fileId, currentLabel) => {
    const labelElement = document.querySelector(
        `.participant-label[data-file-id="${fileId}"]`
    );

    if (!labelElement || labelElement.classList.contains('editing')) {
        return; // Already editing
    }

    // Create input field
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'participant-label-input';
    input.value = currentLabel || '';
    input.placeholder = 'P1, P2, etc.';
    input.maxLength = 10;

    // Save original content
    const originalContent = labelElement.innerHTML;

    // Replace content with input
    labelElement.innerHTML = '';
    labelElement.appendChild(input);
    labelElement.classList.add('editing');
    input.focus();
    input.select();

    // Handle save
    const saveLabel = async () => {
        const newLabel = input.value.trim();

        // Validate format
        if (newLabel && !/^P\d+$/.test(newLabel)) {
            showToast('Error', 'Invalid format. Use P1, P2, P3, etc.', 'error');
            input.focus();
            return;
        }

        // If unchanged, cancel
        if (newLabel === currentLabel) {
            cancelEdit();
            return;
        }

        try {
            // Show loading state
            input.disabled = true;
            labelElement.classList.add('saving');

            // Update via API
            const response = await ApiClient.patch(`/files/${fileId}/participant-label`, {
                participantLabel: newLabel
            });

            // Update UI
            labelElement.textContent = response.file.participant_label;
            labelElement.setAttribute('data-current-label', response.file.participant_label);
            labelElement.classList.remove('editing', 'saving');

            showToast('Success', 'Participant label updated successfully', 'success');

        } catch (error) {
            console.error('Error updating participant label:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response,
                data: error.response?.data,
                status: error.response?.status
            });

            let errorMessage = 'Failed to update label';
            if (error.response?.data?.code === 'DUPLICATE_LABEL') {
                errorMessage = `${newLabel} is already in use`;
            } else if (error.response?.data?.code === 'INVALID_FORMAT') {
                errorMessage = 'Invalid format. Use P1, P2, P3, etc.';
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                // Show the actual error message for debugging
                errorMessage = `Failed to update label: ${error.message}`;
            }

            showToast('Error', errorMessage, 'error');

            // Restore input for retry
            input.disabled = false;
            labelElement.classList.remove('saving');
            input.focus();
            input.select();
        }
    };

    // Handle cancel
    const cancelEdit = () => {
        labelElement.innerHTML = originalContent;
        labelElement.classList.remove('editing', 'saving');
    };

    // Event listeners
    input.addEventListener('blur', () => {
        // Delay to allow click on other elements
        setTimeout(saveLabel, 200);
    });

    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            saveLabel();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelEdit();
        }
    });
};

window.viewFileSummary = (fileId) => {
    const fileData = window.fileSummaries?.[fileId];
    if (!fileData) {
        showToast('Error', 'File summary not found', 'error');
        return;
    }

    const { name: fileName, summary: displaySummary } = fileData;

    // Update file summary page content
    elements.summaryFileName.textContent = fileName;

    if (displaySummary === 'No summary available' || !displaySummary) {
        elements.summaryContentDisplay.innerHTML = '<div class="no-summary">No AI summary available for this file.</div>';
    } else {
        // Use the formatting function to display JSON nicely
        elements.summaryContentDisplay.innerHTML = formatSummaryContent(displaySummary);
    }

    // Store current summary for copy functionality
    window.currentFileSummary = displaySummary;

    // Switch to file summary view
    switchView('fileSummary');
    window.location.hash = `#project/${currentProject.id}/file/${fileId}`;
};
window.deleteQuestion = async (id) => {
    if (confirm('Are you sure you want to delete this question?')) {
        try {
            await ApiClient.delete(`/questions/${id}`);
            showToast('Success', 'Question deleted successfully', 'success');
            loadAdminQuestions();
        } catch (error) {
            showToast('Error', 'Failed to delete question', 'error');
        }
    }
};
window.deleteTemplate = async (id) => {
    if (confirm('Are you sure you want to delete this template?')) {
        try {
            await ApiClient.delete(`/templates/${id}`);
            showToast('Success', 'Template deleted successfully', 'success');
            loadAdminTemplates();
        } catch (error) {
            showToast('Error', 'Failed to delete template', 'error');
        }
    }
};
window.viewTemplate = (name, content) => {
    alert(`Template: ${name}\n\n${content}`);
};
window.downloadTemplate = downloadTemplate;
window.activateTemplate = activateTemplate;

// Expose user management functions globally
window.loadAdminUsers = loadAdminUsers;
window.editUser = editUser;
window.deleteUser = deleteUser;

// ====================
// CSV Import Functionality
// ====================

let csvPreviewData = null;

/**
 * Initialize CSV import event listeners
 */
function initCsvImport() {
    const importCsvBtn = document.getElementById('importCsvBtn');
    const csvImportModal = document.getElementById('csvImportModal');
    const closeCsvImportModal = document.getElementById('closeCsvImportModal');
    const csvFileInput = document.getElementById('csvFileInput');
    const previewCsvBtn = document.getElementById('previewCsvBtn');
    const cancelCsvUploadBtn = document.getElementById('cancelCsvUploadBtn');
    const confirmCsvImportBtn = document.getElementById('confirmCsvImportBtn');
    const backToCsvUploadBtn = document.getElementById('backToCsvUploadBtn');

    // Open modal
    if (importCsvBtn) {
        importCsvBtn.addEventListener('click', () => {
            csvImportModal.style.display = 'block';
            resetCsvImport();
        });
    }

    // Close modal
    if (closeCsvImportModal) {
        closeCsvImportModal.addEventListener('click', () => {
            csvImportModal.style.display = 'none';
        });
    }

    // File input change
    if (csvFileInput) {
        csvFileInput.addEventListener('change', (e) => {
            const file = e.target.files[0];
            previewCsvBtn.disabled = !file;
        });
    }

    // Preview button
    if (previewCsvBtn) {
        previewCsvBtn.addEventListener('click', handleCsvPreview);
    }

    // Cancel upload
    if (cancelCsvUploadBtn) {
        cancelCsvUploadBtn.addEventListener('click', () => {
            csvImportModal.style.display = 'none';
        });
    }

    // Back to upload
    if (backToCsvUploadBtn) {
        backToCsvUploadBtn.addEventListener('click', () => {
            document.getElementById('csvUploadStep').style.display = 'block';
            document.getElementById('csvPreviewStep').style.display = 'none';
        });
    }

    // Confirm import
    if (confirmCsvImportBtn) {
        confirmCsvImportBtn.addEventListener('click', handleCsvImport);
    }

    // Close on outside click
    window.addEventListener('click', (e) => {
        if (e.target === csvImportModal) {
            csvImportModal.style.display = 'none';
        }
    });
}

/**
 * Reset CSV import modal to initial state
 */
function resetCsvImport() {
    document.getElementById('csvUploadStep').style.display = 'block';
    document.getElementById('csvPreviewStep').style.display = 'none';
    document.getElementById('csvFileInput').value = '';
    document.getElementById('previewCsvBtn').disabled = true;
    document.getElementById('csvUploadProgress').style.display = 'none';
    document.getElementById('csvImportProgress').style.display = 'none';
    csvPreviewData = null;
}

/**
 * Handle CSV preview - parse and validate
 */
async function handleCsvPreview() {
    const fileInput = document.getElementById('csvFileInput');
    const file = fileInput.files[0];

    if (!file) {
        showToast('Error', 'Please select a CSV file', 'error');
        return;
    }

    const progressContainer = document.getElementById('csvUploadProgress');
    progressContainer.style.display = 'block';

    const progressBar = new ProgressBar(progressContainer, {
        message: 'Parsing CSV file...',
        showPercentage: true,
        showSteps: true,
        steps: ['Reading File', 'Parsing Data', 'Validating', 'Generating Preview']
    });

    try {
        progressBar.nextStep('Reading file...');

        const formData = new FormData();
        formData.append('file', file);

        progressBar.nextStep('Parsing and validating...');

        // Use direct fetch (not ApiClient) for file upload to avoid Content-Type issues
        const url = `${API_BASE_URL}/projects/${currentProject.id}/categories/import-csv-preview`;
        const token = AuthManager.getToken();

        const fetchResponse = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        });

        if (!fetchResponse.ok) {
            const errorText = await fetchResponse.text();
            let errorMessage = `Upload failed: ${fetchResponse.status}`;
            try {
                const errorJson = JSON.parse(errorText);
                errorMessage = errorJson.error || errorMessage;
            } catch (e) {
                // If not JSON, use the text
                errorMessage = errorText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        const response = await fetchResponse.json();
        csvPreviewData = response;

        progressBar.nextStep('Generating preview...');

        // Show preview
        displayCsvPreview(response);

        progressBar.complete('Preview ready!');

        // Switch to preview step
        setTimeout(() => {
            document.getElementById('csvUploadStep').style.display = 'none';
            document.getElementById('csvPreviewStep').style.display = 'block';
        }, 1000);

    } catch (error) {
        progressBar.reset();
        progressContainer.style.display = 'none';
        console.error('CSV preview error:', error);
        showToast('Error', error.message || 'Failed to parse CSV file', 'error');
    }
}

/**
 * Display CSV preview data
 */
function displayCsvPreview(data) {
    const { summary, categories, questions, errors, warnings } = data;

    // Update summary stats
    document.getElementById('newCategoriesCount').textContent = summary.newCategories || 0;
    document.getElementById('existingCategoriesCount').textContent = summary.existingCategories || 0;
    document.getElementById('newQuestionsCount').textContent = summary.newQuestions || 0;
    document.getElementById('duplicatesCount').textContent = summary.duplicates || 0;

    // Show errors if any
    const errorsContainer = document.getElementById('csvPreviewErrors');
    const errorsList = document.getElementById('csvErrorsList');
    if (errors && errors.length > 0) {
        errorsList.innerHTML = errors.map(err => `<li>${err}</li>`).join('');
        errorsContainer.style.display = 'block';
        document.getElementById('confirmCsvImportBtn').disabled = true;
    } else {
        errorsContainer.style.display = 'none';
        // Check if there's anything to import
        const hasNewData = summary.newQuestions > 0;
        document.getElementById('confirmCsvImportBtn').disabled = !hasNewData;
    }

    // Show warnings if any (including duplicate warning)
    const warningsContainer = document.getElementById('csvPreviewWarnings');
    const warningsList = document.getElementById('csvWarningsList');
    const allWarnings = [...(warnings || [])];

    // Add warning if all questions are duplicates
    if (summary.newQuestions === 0 && summary.duplicates > 0) {
        allWarnings.unshift('All questions already exist in the project. No new data to import.');
    }

    if (allWarnings.length > 0) {
        warningsList.innerHTML = allWarnings.map(warn => `<li>${warn}</li>`).join('');
        warningsContainer.style.display = 'block';
    } else {
        warningsContainer.style.display = 'none';
    }

    // Build preview table
    const previewTable = document.getElementById('csvPreviewTable');
    previewTable.innerHTML = `
        <table class="preview-table">
            <thead>
                <tr>
                    <th>Category</th>
                    <th>Question</th>
                    <th>Status</th>
                </tr>
            </thead>
            <tbody>
                ${renderPreviewRows(categories, questions)}
            </tbody>
        </table>
    `;
}

/**
 * Render preview table rows
 */
function renderPreviewRows(categories, questions) {
    const rows = [];

    Object.keys(questions).forEach(categoryName => {
        const categoryQuestions = questions[categoryName];
        const isNewCategory = categories.new.includes(categoryName);

        categoryQuestions.forEach((q, index) => {
            const categoryCell = index === 0
                ? `<td rowspan="${categoryQuestions.length}">
                     <strong>${categoryName}</strong>
                     ${isNewCategory ? '<span class="badge badge-new">New</span>' : '<span class="badge badge-existing">Existing</span>'}
                   </td>`
                : '';

            rows.push(`
                <tr>
                    ${categoryCell}
                    <td>${q.question}</td>
                    <td>
                        ${q.isDuplicate
                            ? '<span class="badge badge-duplicate">Duplicate (Skip)</span>'
                            : '<span class="badge badge-new">New</span>'}
                    </td>
                </tr>
            `);
        });
    });

    return rows.join('');
}

/**
 * Handle CSV import confirmation
 */
async function handleCsvImport() {
    if (!csvPreviewData) {
        showToast('Error', 'No preview data available', 'error');
        return;
    }

    const progressContainer = document.getElementById('csvImportProgress');
    progressContainer.style.display = 'block';

    const progressBar = new ProgressBar(progressContainer, {
        message: 'Importing categories and questions...',
        showPercentage: true,
        showSteps: true,
        steps: ['Creating Categories', 'Creating Questions', 'Finalizing Import']
    });

    try {
        progressBar.nextStep('Creating categories...');

        const response = await ApiClient.post(
            `/projects/${currentProject.id}/categories/import-csv`,
            { importData: csvPreviewData.importData }
        );

        progressBar.nextStep('Creating questions...');

        await new Promise(resolve => setTimeout(resolve, 500)); // Simulate progress

        progressBar.complete('Import completed!');

        showToast(
            'Success',
            `Successfully imported ${response.summary.categoriesCreated} categories and ${response.summary.questionsCreated} questions`,
            'success'
        );

        // Close modal and refresh categories
        setTimeout(() => {
            document.getElementById('csvImportModal').style.display = 'none';
            loadCategories(currentProject.id); // Refresh categories list
        }, 2000);

    } catch (error) {
        progressBar.reset();
        progressContainer.style.display = 'none';
        console.error('CSV import error:', error);
        showToast('Error', error.message || 'Failed to import CSV data', 'error');
    }
}

// Initialize CSV import when page loads
document.addEventListener('DOMContentLoaded', () => {
    initCsvImport();
});